import { Badge, Box, Modal, Tooltip, Typography } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import OutboxIcon from '@mui/icons-material/Outbox'
import { shopifyProviderPortalTag } from '../../env'
import PhoneNumberInput from '../fields/PhoneNumberInput'
import * as React from 'react'
import {
  List,
  DatagridConfigurable,
  TextField,
  ReferenceArrayField,
  SingleFieldList,
  FunctionField,
  SimpleForm,
  TextInput,
  required,
  TopToolbar,
  Button,
  Create,
  useRefresh,
  Link,
  EditButton,
  useNotify,
  SelectInput,
} from 'react-admin'
import { client } from '../../providers/data/client'
import Empty from '../common/Empty'
import moment from 'moment'

const ProviderAccessList = (props) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const refresh = useRefresh()
  const notify = useNotify()

  const handleCreate = async (event) => {
    try {
      notify('Creating new provider access account', { type: 'info', undoable: false })
      const payload = {
        email: event.email,
        firstName: event.firstName,
        lastName: event.lastName,
        phone: event.phone,
      }
      const result = await client().post('/provider-access/users', payload)

      if (result) {
        setIsOpen(false)
        notify('Provider access account created', { type: 'success', undoable: false })
        refresh()
      }
    } catch (error) {
      let errorMessage = 'Failed to create provider access account.'

      if (error.response.data.message) {
        errorMessage = error.response.data.message
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }

      notify(errorMessage, { type: 'error', multiLine: true })
    }
  }

  const CreateModal = () => {
    return (
      <Modal className="modal" open={isOpen} onClose={() => setIsOpen(false)}>
        <Box className="modalContainer">
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Create a new provider access account and invite them to join:
          </Typography>
          <Create resource="provider-access/users" redirect={false} sx={{ height: '100%' }}>
            <SimpleForm onSubmit={(event) => handleCreate(event)}>
              <TextInput source="email" validate={required()} fullWidth />
              <TextInput source="firstName" label="First Name" validate={required()} fullWidth />
              <TextInput source="lastName" label="Last Name" validate={required()} fullWidth />
              <PhoneNumberInput source="phone" label="Phone" fullWidth />
            </SimpleForm>
          </Create>
        </Box>
      </Modal>
    )
  }

  const handleProviderPortalAccess = async (record, hasAccess) => {
    try {
      if (hasAccess) {
        const result = await client().post(`/provider-access/remove-access/${record.id}`, {})
        if (result) notify('Access removed for provider portal')
      } else {
        const result = await client().post(`/provider-access/grant-access/${record.id}`, {})
        if (result) notify('Access given for provider portal')
      }
      refresh()
    } catch (error) {
      let errorMessage = 'Failed to update provider access.'

      if (error.response.data.message) {
        errorMessage = error.response.data.message
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }

      notify(errorMessage, { type: 'error', multiLine: true })
      // Refresh so the list reflects true server state even when the API errored,
      // preventing the admin from acting on a stale "not granted" view.
      refresh()
    }
  }

  const handleSendInvite = async (recordId) => {
    try {
      const result = await client().post(`/provider-access/send-activation/${recordId}/`, {})
      if (result) notify('Invite sent')
      refresh()
    } catch (error) {
      let errorMessage = 'Failed to send invite.'

      if (error.response.data.message) {
        errorMessage = error.response.data.message
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }

      notify(errorMessage, { type: 'error', multiLine: true })
    }
  }

  const handleSendReminder = async (recordId) => {
    if (
      window.confirm(
        'Are you sure you want to send a welcome email to customerId ' +
        recordId +
        ' with details on how to access the provider portal?'
      ) === false
    ) {
      return
    }

    try {
      const result = await client().post(`/provider-access/send-welcome/${recordId}/`, {})
      if (result) notify('Welcome email sent')
      refresh()
    } catch (error) {
      let errorMessage = 'Failed to send reminder.'

      if (error.response.data.message) {
        errorMessage = error.response.data.message
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }

      notify(errorMessage, { type: 'error', multiLine: true })
    }
  }

  const handleModalOpen = () => setIsOpen(true)

  return (
    <>
      <List
        filterDefaultValues={{ pp_tags: shopifyProviderPortalTag }}
        resource="provider-access/users"
        {...props}
        actions={
          <TopToolbar>
            <Button label="Send Invite" children={<EmailIcon />} variant="contained" color="primary" onClick={handleModalOpen} />
          </TopToolbar>
        }
        filter={{ _order: 'DESC' }}
        filters={[
          <TextInput
            label="Email / Name / Company"
            validate={(value) => {
              if (value && !/^[a-zA-Z0-9\-,@._\s]*$/.test(value)) {
                return 'Only letters, numbers, hyphens, commas and spaces are allowed'
              }
              return undefined
            }}
            source="pp_search"
            alwaysOn
          />,
          <SelectInput
            source="pp_tags"
            label="Access Status"
            emptyText="All"
            choices={[{ id: shopifyProviderPortalTag, name: 'Has Portal Access' }]}
            alwaysOn
          />,
        ]}
        empty={
          <Empty
            create={true}
            CreateComponent={() => <Button variant="contained" label="Send Invite" size="medium" onClick={handleModalOpen} />}
          />
        }
      >
        <DatagridConfigurable bulkActionButtons={false}>
          <FunctionField label="Email" render={(record) => <Link to={`/provider-access/${record.id}/show`}>{record.email}</Link>} />
          <FunctionField
            label="Shopify Acct Info"
            render={(record) => {
              return (
                <div>
                  <div style={{ background: '#f0ad4e26', padding: '4px' }}>
                    <p>
                      Name: <b>{record.first_name + ' ' + record.last_name ?? 'Not Added'}</b>
                    </p>
                    <p>
                      Created: <b>{record.created_at ? moment(record.created_at).format('YYYY-MM-DD') : '-'}</b>
                    </p>
                    <p>
                      Shopify Orders: <b>{record.orders_count ?? '-'}</b>
                    </p>
                    <p>
                      Access to Provider Portal: <b>{record.tags.includes(shopifyProviderPortalTag) ? 'Yes' : 'No'}</b>
                    </p>
                    <p>
                      Account State: <b>{record.state}</b>
                    </p>
                  </div>
                </div>
              )
            }}
          />
          <FunctionField
            label="Last logged in"
            render={(record) => (record.lastLoggedIn ? moment(record.lastLoggedIn).format('YYYY-MM-DD HH:mm:ss') : null)}
          />
          <FunctionField
            label="Kit Requests"
            render={(record) =>
              record.kitRequests ? (
                <Tooltip
                  title={`User can initiate Kit Request(s)${Array.isArray(record.providerGroupIds) ? ` for ${record.providerGroupIds.length} provider group(s)` : ''}`}
                >
                  <Badge
                    badgeContent={Array.isArray(record.providerGroupIds) ? record.providerGroupIds.length : 0}
                    color="primary"
                    sx={{ '& .MuiBadge-badge': { right: -2, top: 3, fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                  >
                    <OutboxIcon fontSize="small" />
                  </Badge>
                </Tooltip>
              ) : null
            }
          />
          <ReferenceArrayField link="show" label="Providers" source="providers" reference="providers">
            <SingleFieldList style={{ display: 'flex', flexFlow: 'column', rowGap: '2px' }}>
              <TextField source="displayName" />
            </SingleFieldList>
          </ReferenceArrayField>
          <FunctionField
            label="Notification"
            render={(record) => {
              return record.notification?.length > 0 ? (
                <div className="text-success">
                  <i className="fa-solid fa-envelope"></i>
                  <span style={{ padding: '0 4px' }}>{record.notification?.length}</span>
                </div>
              ) : null
            }}
          />
          <FunctionField
            label={'Actions'}
            render={(record) => {
              const hasAccess = record.tags.includes(shopifyProviderPortalTag)

              return (
                <div style={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}>
                  {hasAccess && <EditButton sx={{ width: '100%' }} variant="outlined" resource="provider-access" />}
                  <Button
                    variant="contained"
                    size="medium"
                    sx={{ fontSize: '12px', width: '100%' }}
                    color={hasAccess ? 'error' : 'primary'}
                    label={hasAccess ? 'Remove Access to Provider Portal' : 'Give Access to Provider Portal'}
                    onClick={() => handleProviderPortalAccess(record, hasAccess)}
                  />

                  {record.state?.toLowerCase() === 'disabled' && (
                    <Button
                      variant="contained"
                      size="medium"
                      sx={{ fontSize: '12px', width: '100%' }}
                      color="primary"
                      label="Send Invite"
                      onClick={() => handleSendInvite(record.id)}
                    />
                  )}

                  {['enabled', 'invited'].includes((record.state ?? '').toLowerCase()) &&
                    record.tags.includes(shopifyProviderPortalTag) && (
                      /* Option to send email to enabled or invited provider portal users ... */
                      <Button
                        variant="contained"
                        size="medium"
                        sx={{ fontSize: '12px', width: '100%' }}
                        color="grey"
                        label="Send Provider Welcome Email"
                        onClick={() => handleSendReminder(record.id)}
                      />
                    )}
                </div>
              )
            }}
          />
        </DatagridConfigurable>
      </List>
      <CreateModal />
    </>
  )
}

export default ProviderAccessList
